// Third Party
import React, { useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import throttle from 'lodash/throttle';

// Components
import HistoricalScenariosComparisonScenario from './components/HistoricalScenariosComparisonScenario';

// Utils
import { COMPARISON_CARD_WIDTH, COMPARISON_CARD_WIDTH_SMALL } from '@/utils/constants';

// Types
import ComparisonItem from '@/types/comparison-item';
import Nullable from '@/types/nullable';
import User from '@/types/user';
import Comparison from '@/types/comparison';

type DispatchProps = {
  updateComparisonItemColor: (item: ComparisonItem, color: string) => void;
};

type Props = DispatchProps & {
  item: ComparisonItem;
  user?: Nullable<User>;
  isEditPinned: boolean;
  comparison: Comparison;
  popoverContainerRef: Nullable<HTMLDivElement>;
  scrollbarContainerRef?: HTMLElement;
};

const HistoricalScenariosStreamCard: React.FC<Props> = ({
  item,
  user,
  isEditPinned,
  comparison,
  updateComparisonItemColor,
  popoverContainerRef,
  scrollbarContainerRef,
}) => {
  const [cardWidth, setCardWidth] = useState(0);

  const adjustCardWidth = useCallback(() => {
    // Must calc width for income stream cards based on dynamic num of scenarios. Used for the transition state when editing a portfolio.
    const windowWidth = isEditPinned ? window.innerWidth - 480 : window.innerWidth;
    const cardWidthBase = windowWidth < 1100 ? COMPARISON_CARD_WIDTH_SMALL : COMPARISON_CARD_WIDTH;

    if (!item.scenarios || !item.scenarios.incomeStreamDistribution) {
      setCardWidth(cardWidthBase);
      return;
    }
    const width = item.scenarios.incomeStreamDistribution.reduce((acc, curr) => acc + cardWidthBase, cardWidthBase);
    setCardWidth(width);
  }, [item, isEditPinned]);

  // Throttle the resize handler
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleResize = useCallback(throttle(adjustCardWidth, 20), [adjustCardWidth]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    adjustCardWidth();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize, adjustCardWidth]);

  useEffect(() => {
    // Watch for changes in item.scenarios.incomeStreamDistribution length
    adjustCardWidth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.scenarios?.incomeStreamDistribution?.length]);

  const cardClasses = classNames('historical-scenario-card', {
    shared: !!item.providedBy,
  });

  return (
    <div className={cardClasses} key={`${item._id}`} style={{ width: cardWidth, transition: 'unset' }} id={`card-${item._id}`}>
      <HistoricalScenariosComparisonScenario
        className="comparison-scenario"
        user={user}
        item={item}
        comparison={comparison}
        updateComparisonItemColor={updateComparisonItemColor}
        popoverContainerRef={popoverContainerRef}
        scrollbarContainerRef={scrollbarContainerRef}
      />
    </div>
  );
};

export default HistoricalScenariosStreamCard;
